import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import http, { formatApiErrorDetail } from "@/api";
import { useAuth } from "@/auth";
import { PILLARS } from "@/constants";
import { useCategories } from "@/hooks/useCategories";
import CategoryMultiSelect from "@/components/CategoryMultiSelect";
import {
  ArrowLeft, X, Save, ShieldCheck, Eye, Download, Edit3, Link2,
  Users, FileClock, Star, AlertCircle, Search,
} from "lucide-react";
import Modal from "@/components/Modal";

const ROLE_META = {
  admin:       { label: "Administrador",            color: "bg-[var(--lala-red,#E0162B)] text-white" },
  evaluator:   { label: "Evaluador",                color: "bg-[var(--lala-blue)] text-[var(--cream)]" },
  stakeholder: { label: "Visualizador",             color: "bg-amber-500 text-white" },
  buyer:       { label: "Category Manager",         color: "bg-emerald-600 text-white" },
  supplier:    { label: "Proveedor",                color: "bg-stone-500 text-white" },
  tech_admin:  { label: "TI",                       color: "bg-slate-600 text-white" },
  owner:       { label: "DEV",                      color: "bg-black text-white" },
};

const PERMISSION_META = {
  can_view:             { label: "Ver", icon: Eye },
  can_download_reports: { label: "Descargar", icon: Download },
  can_edit_evaluations: { label: "Editar", icon: Edit3 },
  can_invite_suppliers: { label: "Invitar", icon: Link2 },
};

const SUBAREAS_INNOVACION = ["I&D Ingredientes", "Innovación Empaques", "Innovación MKT"];

// Normaliza para comparar sin mayúsculas ni acentos ("jesus" encuentra "Jesús").
const normalizeText = (s) => (s || "").normalize("NFD").replace(/[̀-ͯ]/g, "").toLowerCase().trim();

function UserFormModal({ initial, onClose, onSaved }) {
  const isEdit = !!initial?.id;
  const categorias = useCategories();
  const [f, setF] = useState({
    name: initial?.name || "", email: initial?.email || "",
    role: initial?.role || "evaluator",
    assigned_pillars: initial?.assigned_pillars || [],
    assigned_categories: initial?.assigned_categories || [],
    plant: initial?.plant || "", innovation_subarea: initial?.innovation_subarea || "",
  });
  const [plantas, setPlantas] = useState([]);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [tempPwd, setTempPwd] = useState(null);
  useEffect(() => { http.get("/plantas").then((r) => setPlantas(r.data.plantas || r.data || [])).catch(() => {}); }, []);

  const togglePillar = (p) => setF((s) => ({
    ...s, assigned_pillars: s.assigned_pillars.includes(p)
      ? s.assigned_pillars.filter((x) => x !== p) : [...s.assigned_pillars, p],
  }));
  // "Todas" = sin restricción de categoría (lista vacía); excluye a las demás, igual que en el Catálogo de KPIs.
  const toggleCat = (c) => setF((s) => {
    const cur = s.assigned_categories;
    const next = c === "Todas" ? [] : cur.includes(c) ? cur.filter((x) => x !== c) : [...cur, c];
    return { ...s, assigned_categories: next };
  });
  const showCats = f.role === "evaluator";
  const showSub = f.role === "evaluator" && f.assigned_pillars.includes("innovacion");
  const showPlant = f.role === "evaluator" && f.assigned_pillars.includes("relacion");

  const save = async () => {
    setBusy(true); setErr("");
    try {
      const payload = {
        name: f.name, role: f.role,
        assigned_pillars: f.role === "evaluator" ? f.assigned_pillars : [],
        assigned_categories: showCats ? f.assigned_categories : [],
        plant: showPlant ? f.plant : null,
        innovation_subarea: showSub ? f.innovation_subarea : null,
      };
      if (isEdit) {
        await http.put(`/admin/users/${initial.id}/profile`, payload);
        onSaved(); onClose();
      } else {
        const r = await http.post("/admin/users", { ...payload, email: f.email });
        setTempPwd(r.data.temp_password);
        onSaved();
      }
    } catch (e) {
      setErr(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally { setBusy(false); }
  };

  return (
    <Modal testId="user-form-modal" align="center">
      <div className="sketch-border bg-white p-6 max-w-lg w-full">
        <div className="font-display text-xl text-[var(--lala-blue)] mb-3">{isEdit ? `Editar · ${initial.name}` : "Crear usuario"}</div>
        {tempPwd ? (
          <div className="space-y-3" data-testid="user-created-panel">
            <div className="text-sm bg-emerald-50 border border-emerald-300 rounded-lg p-3">
              Usuario creado. Entrega esta contraseña temporal de forma segura — <b>no se volverá a mostrar</b> y deberá cambiarla en su primer inicio de sesión.
            </div>
            <div className="font-mono text-lg bg-[var(--cream-soft)] border border-[var(--line)] rounded-lg p-3 flex items-center justify-between" data-testid="user-temp-password">
              {tempPwd}
              <button className="sketch-pill px-3 py-1 text-xs font-semibold" onClick={() => navigator.clipboard?.writeText(tempPwd)} data-testid="copy-temp-password">Copiar</button>
            </div>
            <button className="btn-primary w-full text-sm" onClick={onClose} data-testid="user-form-done">Listo</button>
          </div>
        ) : (
        <div className="space-y-3 text-sm">
          <input value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} placeholder="Nombre completo"
            className="w-full border border-[var(--line)] rounded-lg px-3 py-2" data-testid="uf-name" />
          <input value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} placeholder="correo@grupolala.com"
            disabled={isEdit} className="w-full border border-[var(--line)] rounded-lg px-3 py-2 disabled:opacity-50" data-testid="uf-email" />
          <select value={f.role} onChange={(e) => setF({ ...f, role: e.target.value })}
            className="w-full border border-[var(--line)] rounded-lg px-3 py-2" data-testid="uf-role">
            <option value="evaluator">Evaluador (edita su pilar, consulta y descarga todo)</option>
            <option value="stakeholder">Stakeholder / Visualizador (solo consulta y descarga)</option>
            <option value="admin">Admin (acceso completo)</option>
          </select>
          {f.role === "evaluator" && (
            <div>
              <div className="text-[11px] uppercase tracking-wider font-bold text-[var(--ink-soft)] mb-1">Pilar(es) editable(s)</div>
              <div className="flex flex-wrap gap-2">
                {PILLARS.map((p) => (
                  <button key={p.key} onClick={() => togglePillar(p.key)}
                    className={`sketch-pill px-3 py-1 text-xs font-semibold ${f.assigned_pillars.includes(p.key) ? "bg-[var(--lala-blue)] text-white" : ""}`}
                    data-testid={`uf-pillar-${p.key}`}>{p.label}</button>
                ))}
              </div>
            </div>
          )}
          {showCats && (
            <div>
              <div className="text-[11px] uppercase tracking-wider font-bold text-[var(--ink-soft)] mb-1">Categorías de compra (opcional)</div>
              <CategoryMultiSelect options={["Todas", ...categorias]} testId="uf-cats"
                selected={f.assigned_categories.length ? f.assigned_categories : ["Todas"]} onToggle={toggleCat} />
            </div>
          )}
          {showSub && (
            <select value={f.innovation_subarea} onChange={(e) => setF({ ...f, innovation_subarea: e.target.value })}
              className="w-full border border-[var(--line)] rounded-lg px-3 py-2" data-testid="uf-subarea">
              <option value="">Subárea de Innovación (asignación organizacional)…</option>
              {SUBAREAS_INNOVACION.map((s) => <option key={s} value={s}>{s}</option>)}
            </select>
          )}
          {showPlant && (
            <select value={f.plant} onChange={(e) => setF({ ...f, plant: e.target.value })}
              className="w-full border border-[var(--line)] rounded-lg px-3 py-2" data-testid="uf-plant">
              <option value="">Planta (edición limitada a Nivel de Servicio de esa planta)…</option>
              {plantas.map((p) => <option key={p} value={p}>{p}</option>)}
            </select>
          )}
          {err && <div className="text-red-700 bg-red-50 border border-red-200 rounded-lg p-2" data-testid="uf-error">{err}</div>}
          <div className="flex gap-2">
            <button onClick={save} disabled={busy || !f.name || (!isEdit && !f.email)}
              className="btn-primary flex-1 text-sm" data-testid="uf-save">
              {busy ? "Guardando…" : isEdit ? "Guardar cambios (sin tocar contraseña)" : "Crear usuario"}
            </button>
            <button onClick={onClose} className="sketch-pill px-4 py-1.5 text-sm font-semibold" data-testid="uf-cancel">Cancelar</button>
          </div>
        </div>
        )}
      </div>
    </Modal>
  );
}

const PILLAR_LABEL = Object.fromEntries(PILLARS.map((p) => [p.key, p.label]));

function ProvisionCard({ onApplied }) {
  const [plan, setPlan] = useState(null);
  const [result, setResult] = useState(null);
  const [status, setStatus] = useState(null);
  const [busy, setBusy] = useState("");
  const [perr, setPerr] = useState("");

  const loadStatus = async () => {
    try {
      const r = await http.get("/admin/users/provision/status");
      setStatus(r.data);
    } catch (_) {}
  };
  useEffect(() => { loadStatus(); }, []);

  const runPlan = async () => {
    setBusy("plan"); setPerr(""); setResult(null);
    try {
      const { provisioning } = await import("@/api");
      setPlan(await provisioning.plan());
    } catch (e) {
      setPerr(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally { setBusy(""); }
  };

  const runApply = async () => {
    setBusy("apply"); setPerr("");
    try {
      const { provisioning } = await import("@/api");
      const r = await provisioning.apply();
      setResult(r);
      setPlan(null);
      onApplied && onApplied();
    } catch (e) {
      setPerr(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally { setBusy(""); }
  };

  const downloadCreds = async (regenerate = false) => {
    setBusy("creds"); setPerr("");
    try {
      const { provisioning } = await import("@/api");
      const resp = await provisioning.downloadCredentials(regenerate);
      const url = window.URL.createObjectURL(resp.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = "credenciales_lala_elite.xlsx";
      a.click();
      window.URL.revokeObjectURL(url);
      await loadStatus();
    } catch (e) {
      let detail = e.response?.data?.detail;
      if (e.response?.data instanceof Blob) {
        try { detail = JSON.parse(await e.response.data.text()).detail; } catch (_) {}
      }
      setPerr(formatApiErrorDetail(detail) || e.message);
    } finally { setBusy(""); }
  };

  return (
    <div className="sketch-border bg-white p-5 mb-8" data-testid="provision-card">
      <div className="font-display text-lg text-[var(--lala-blue)] mb-1">Carga inicial de usuarios (Excel 270826)</div>
      <p className="text-sm text-[var(--ink-soft)] mb-4">
        Roster oficial: 80 usuarios (1 admin, 29 evaluadores, 50 stakeholders). La carga es idempotente por correo:
        crea nuevos y actualiza rol/pilares/planta sin tocar contraseñas de usuarios existentes.
      </p>
      {status && (
        <div className="mb-3 text-sm" data-testid="provision-creds-status">
          {status.state === "no_generadas" && (
            <span className="px-2 py-1 rounded-md bg-stone-100 text-stone-700 border border-stone-300 font-semibold">
              Credenciales: aún no generadas
            </span>
          )}
          {status.state === "pendientes_por_emitir" && (
            <span className="px-2 py-1 rounded-md bg-amber-50 text-amber-900 border border-amber-300 font-semibold">
              Credenciales: {status.pending} pendientes por emitir — disponibles para descarga única
            </span>
          )}
          {status.state === "ya_descargadas" && (
            <span className="px-2 py-1 rounded-md bg-emerald-50 text-emerald-800 border border-emerald-300 font-semibold">
              Credenciales: ya descargadas ({status.last_issued_at?.slice(0, 16).replace("T", " ")} UTC). Para reemitir se requiere regeneración autorizada (solo usuarios que nunca han iniciado sesión: {status.never_logged_in}).
            </span>
          )}
        </div>
      )}
      <div className="flex flex-wrap gap-2 mb-4">
        <button onClick={runPlan} disabled={!!busy} className="sketch-pill px-4 py-1.5 text-sm font-semibold" data-testid="provision-plan-btn">
          {busy === "plan" ? "Analizando…" : "1 · Analizar carga (sin cambios)"}
        </button>
        <button onClick={runApply} disabled={!!busy || !plan || !plan.totales_coinciden}
          className="btn-primary text-sm" data-testid="provision-apply-btn">
          {busy === "apply" ? "Ejecutando…" : "2 · Ejecutar carga"}
        </button>
        <button onClick={() => downloadCreds(false)} disabled={!!busy || status?.state === "ya_descargadas"}
          className="sketch-pill px-4 py-1.5 text-sm font-semibold disabled:opacity-40 disabled:cursor-not-allowed"
          title={status?.state === "ya_descargadas" ? "Ya fueron descargadas; requiere regeneración autorizada" : ""}
          data-testid="provision-creds-btn">
          {busy === "creds" ? "Generando…" : "3 · Descargar credenciales (única vez)"}
        </button>
      </div>
      {perr && <div className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-lg p-2 mb-3" data-testid="provision-err">{perr}</div>}
      {plan && (
        <div className="text-sm bg-[var(--cream-soft)] rounded-lg p-3 space-y-1" data-testid="provision-plan-summary">
          <div><b>Leídos:</b> {plan.leidos} · <b>Por rol:</b> admin {plan.por_rol.admin || 0} / evaluadores {plan.por_rol.evaluator || 0} / stakeholders {plan.por_rol.stakeholder || 0}</div>
          <div><b>Nuevos:</b> {plan.nuevos.length} · <b>A actualizar:</b> {plan.actualizar.length} · <b>Sin cambios:</b> {plan.sin_cambios} · <b>Errores:</b> {plan.errores.length}</div>
          <div className={plan.totales_coinciden ? "text-emerald-700 font-semibold" : "text-red-700 font-semibold"}>
            {plan.totales_coinciden ? "✔ Totales coinciden con lo esperado (80/1/29/50)" : "✖ Totales NO coinciden — carga bloqueada"}
          </div>
          {plan.actualizar.length > 0 && (
            <details className="mt-1"><summary className="cursor-pointer">Ver campos que cambiarán</summary>
              <pre className="text-[11px] overflow-auto max-h-48 mt-1">{JSON.stringify(plan.actualizar, null, 1)}</pre>
            </details>
          )}
        </div>
      )}
      {result && (
        <div className="text-sm bg-emerald-50 border border-emerald-200 rounded-lg p-3" data-testid="provision-result">
          <b>Carga ejecutada:</b> {result.creados.length} creados · {result.actualizados.length} actualizados · {result.omitidos} sin cambios · {result.errores.length} errores.
          Ahora descarga las credenciales temporales (paso 3).
        </div>
      )}
    </div>
  );
}

export default function UsersAdmin() {
  const { user } = useAuth();
  const nav = useNavigate();
  const [users, setUsers] = useState([]);
  const [editing, setEditing] = useState(null);
  const [formUser, setFormUser] = useState(null);
  const [showAudit, setShowAudit] = useState(null);
  const [auditRows, setAuditRows] = useState([]);
  const [allSuppliers, setAllSuppliers] = useState([]);
  const [msg, setMsg] = useState("");
  const [err, setErr] = useState("");

  const [nameFilter, setNameFilter] = useState("");
  useEffect(() => {
    if (user && user.role !== "admin" && !user.is_category_lead) nav("/dashboard");
  }, [user, nav]);

  const refresh = () =>
    Promise.all([
      http.get("/admin/users"),
      http.get("/suppliers"),
    ]).then(([u, s]) => {
      setUsers(u.data || []);
      setAllSuppliers(s.data || []);
    });

  useEffect(() => { refresh().catch(() => {}); }, []);

  const filteredUsers = useMemo(() => {
    const q = normalizeText(nameFilter);
    return q ? users.filter((u) => normalizeText(u.name).includes(q)) : users;
  }, [users, nameFilter]);
  const internal = useMemo(() => filteredUsers.filter((u) => u.role !== "supplier"), [filteredUsers]);
  const suppliers = useMemo(() => filteredUsers.filter((u) => u.role === "supplier"), [filteredUsers]);

  const openAudit = async (u) => {
    setShowAudit(u);
    try {
      const r = await http.get("/admin/audit-log", { params: { user_id: u.id, limit: 100 } });
      setAuditRows(r.data || []);
    } catch (e) {
      setAuditRows([]);
    }
  };

  const saveScope = async (u, scope) => {
    setErr(""); setMsg("");
    try {
      await http.put(`/admin/users/${u.id}/scope`, scope);
      await refresh();
      setMsg(`Scope actualizado para ${u.name}.`);
    } catch (e) {
      setErr(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    }
  };

  const savePermissions = async (u, perms) => {
    setErr(""); setMsg("");
    try {
      await http.put(`/admin/users/${u.id}/permissions`, perms);
      await refresh();
      setMsg(`Permisos actualizados para ${u.name}.`);
    } catch (e) {
      setErr(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    }
  };

  return (
    <div className="max-w-6xl mx-auto px-6 py-10">
      <Link to="/admin/configuracion" data-testid="back-config"
        className="text-sm text-[var(--lala-blue)] inline-flex items-center gap-1 hover:underline">
        <ArrowLeft size={14} /> Volver a configuración
      </Link>

      <div className="mt-2 mb-8">
        <div className="font-hand text-xl text-[var(--lala-blue)]">enterprise permissions</div>
        <h1 className="font-display text-4xl inline-flex items-center gap-3">
          <Users size={36} /> Usuarios y permisos
        </h1>
        <p className="text-[var(--ink-soft)] mt-1 max-w-3xl">
          Asigna proveedores y categorías, controla quién puede ver, descargar reportes, editar evaluaciones e invitar
          proveedores. Cada acción queda registrada en la bitácora.
        </p>
      </div>

      <div className="sketch-border bg-[var(--cream-soft)] p-5 mb-8" data-testid="how-permissions-work">
        <div className="font-display text-lg text-[var(--lala-blue)] mb-3">¿Cómo funciona?</div>
        <div className="grid md:grid-cols-2 gap-x-8 gap-y-3 text-sm">
          <Helper title="Asignación por categoría" body="Marca categorías oficiales del programa (Asépticos, Empaques, Ingredientes, Materia prima cárnica, Maquilas Co-Man, Maquilas Co-Pack). El usuario verá automáticamente los proveedores de esas categorías." />
          <Helper title="Asignación por proveedor" body="Si necesitas excepciones puntuales, agrega proveedores individuales que se suman a las categorías." />
          <Helper title="Líder de categoría" body="Toggle que permite a un usuario administrar a otros usuarios dentro de su scope. Solo admin puede otorgarlo." />
          <Helper title="Permisos granulares" body="Cada usuario tiene 4 toggles: ver, descargar reportes, editar evaluaciones e invitar proveedores. Default por rol." />
          <Helper title="Pilares del evaluador" body="Asigna a cada evaluador el/los pilares que alimenta (p. ej. Innovación). Solo verá y capturará indicadores de esos pilares en Captura mensual." />
        </div>
      </div>

      {msg && <div className="mb-4 text-green-700 bg-green-50 border border-green-200 rounded-lg p-3 text-sm" data-testid="users-msg">{msg}</div>}
      {err && <div className="mb-4 text-red-700 bg-red-50 border border-red-200 rounded-lg p-3 text-sm" data-testid="users-err">{err}</div>}

      {user?.role === "admin" && <ProvisionCard onApplied={refresh} />}

      <h2 className="font-display text-2xl mb-3 flex items-center justify-between">
        Equipo interno Lala
        {user?.role === "admin" && (
          <button onClick={() => setFormUser({})} className="btn-primary text-sm" data-testid="create-user-btn">
            + Crear usuario
          </button>
        )}
      </h2>
      {formUser !== null && (
        <UserFormModal initial={formUser.id ? formUser : null}
          onClose={() => setFormUser(null)} onSaved={refresh} />
      )}
      <div className="space-y-3 mb-10" data-testid="internal-users-list">
      <div className="relative mb-4 max-w-md">
        <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--ink-soft)] pointer-events-none" />
        <input type="text" value={nameFilter} onChange={(e) => setNameFilter(e.target.value)}
          placeholder="Filtrar por nombre…" aria-label="Filtrar usuarios por nombre"
          className="input-field w-full !pl-9 !pr-9" data-testid="users-name-filter" />
        {nameFilter && (
          <button type="button" onClick={() => setNameFilter("")} aria-label="Limpiar filtro"
            className="absolute right-2 top-1/2 -translate-y-1/2 p-1 rounded hover:bg-[var(--cream-soft)]"
            data-testid="users-name-filter-clear">
            <X size={14} />
          </button>
        )}
      </div>
        {internal.map((u) => (
          <UserRow key={u.id} u={u}
            onEdit={() => setEditing(u)}
            onProfile={user?.role === "admin" ? () => setFormUser(u) : null}
            onAudit={() => openAudit(u)} />
        ))}
        {internal.length === 0 && (
          <div className="text-[var(--ink-soft)] italic">
            {nameFilter ? `Sin usuarios internos que coincidan con "${nameFilter}".` : "No hay usuarios internos."}
          </div>
        )}
      </div>

      <h2 className="font-display text-2xl mb-3">Proveedores con cuenta</h2>
      <div className="space-y-3" data-testid="supplier-users-list">
        {suppliers.map((u) => (
          <UserRow key={u.id} u={u} disabled
            onEdit={null}
            onAudit={() => openAudit(u)} />
        ))}
        {suppliers.length === 0 && (
          <div className="text-[var(--ink-soft)] italic">
            {nameFilter ? `Sin proveedores que coincidan con "${nameFilter}".` : "No hay proveedores con cuenta vinculada."}
          </div>
        )}
      </div>

      {editing && (
        <UserEditor user={editing}
          allSuppliers={allSuppliers}
          actorIsAdmin={user?.role === "admin"}
          onCancel={() => setEditing(null)}
          onSaveScope={(scope) => saveScope(editing, scope).then(() => setEditing(null))}
          onSavePerms={(perms) => savePermissions(editing, perms).then(() => setEditing(null))} />
      )}
      {showAudit && (
        <AuditDrawer user={showAudit} rows={auditRows} onClose={() => setShowAudit(null)} />
      )}
    </div>
  );
}

function Helper({ title, body }) {
  return (
    <div className="flex items-start gap-2">
      <ShieldCheck size={14} className="text-[var(--lala-blue)] mt-0.5 shrink-0" />
      <div>
        <div className="font-semibold text-[var(--ink)]">{title}</div>
        <div className="text-[var(--ink-soft)]">{body}</div>
      </div>
    </div>
  );
}

function UserRow({ u, onEdit, onProfile, onAudit, disabled }) {
  const rm = ROLE_META[u.role] || ROLE_META.stakeholder;
  const scope = u.assigned_categories || [];
  const indivs = (u.assigned_supplier_ids || []).length;
  const perms = u.permissions || {};
  return (
    <div className="sketch-border-soft bg-[var(--cream)] p-4 flex items-start gap-3"
      data-testid={`user-row-${u.id}`}>
      <div className="shrink-0 w-10 h-10 rounded-full bg-[var(--lala-blue)] text-[var(--cream)] flex items-center justify-center font-bold">
        {(u.name || "?").slice(0, 1)}
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex flex-wrap items-center gap-2 mb-1">
          <span className="font-display text-lg">{u.name}</span>
          {u.is_test_account && (
            <span className="px-2 py-0.5 rounded-md bg-stone-200 text-stone-700 border border-stone-400 text-[10px] font-bold uppercase tracking-wider"
              data-testid={`user-test-badge-${u.id}`}>
              Cuenta de prueba · no migra a producción
            </span>
          )}
          <span className={`text-[10px] uppercase tracking-wider font-semibold px-2 py-0.5 rounded-full ${rm.color}`}>
            {rm.label}
          </span>
          {u.is_category_lead && (
            <span className="text-[10px] uppercase tracking-wider font-semibold px-2 py-0.5 rounded-full bg-amber-100 text-amber-900 border border-amber-300 inline-flex items-center gap-1">
              <Star size={10} /> Líder
            </span>
          )}
        </div>
        <div className="text-xs text-[var(--ink-soft)]">{u.email}</div>
        <div className="mt-2 flex flex-wrap gap-1.5 text-xs">
          {u.role !== "supplier" && (
            <>
              {(u.assigned_pillars || []).map((p) => (
                <span key={p} data-testid={`user-pillar-${u.id}-${p}`}
                  className="px-2 py-0.5 rounded-md bg-[var(--lala-blue-soft)] text-[var(--lala-blue)] border border-[var(--lala-blue)]/40 font-semibold">
                  ◆ {PILLAR_LABEL[p] || p}
                </span>
              ))}
              {u.plant && (
                <span data-testid={`user-plant-${u.id}`}
                  className="px-2 py-0.5 rounded-md bg-emerald-50 text-emerald-800 border border-emerald-300 font-semibold">
                  Edición limitada a: {u.plant}
                </span>
              )}
              {u.plant && (
                <span className="px-2 py-0.5 rounded-md bg-[var(--cream-soft)] text-[var(--ink-soft)] border border-[var(--line)]"
                  data-testid={`user-plant-scope-note-${u.id}`}>
                  Consulta: todos los pilares · Proveedores visibles: sin restricción hasta recibir el mapeo proveedor–planta
                </span>
              )}
              {scope.length === 0 && indivs === 0 && !u.plant ? (
                <span className="px-2 py-0.5 rounded-md bg-[var(--lala-blue-soft)] text-[var(--lala-blue)] border border-[var(--lala-blue)]/40">
                  Acceso global (sin scope)
                </span>
              ) : (
                <>
                  {scope.map((c) => (
                    <span key={c} className="px-2 py-0.5 rounded-md bg-amber-50 text-amber-900 border border-amber-300">
                      {c}
                    </span>
                  ))}
                  {indivs > 0 && (
                    <span className="px-2 py-0.5 rounded-md bg-stone-100 text-stone-700 border border-stone-300">
                      + {indivs} proveedor{indivs === 1 ? "" : "es"} específicos
                    </span>
                  )}
                </>
              )}
              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md bg-white border border-[var(--line)] text-[var(--ink-soft)]">
                {Object.entries(PERMISSION_META).map(([k, m]) => {
                  const has = perms[k] !== undefined ? perms[k] : permissionDefault(u.role, k);
                  const Icon = m.icon;
                  return (
                    <span key={k} title={m.label} className={has ? "text-[var(--lala-blue)]" : "text-stone-300 line-through"}>
                      <Icon size={12} />
                    </span>
                  );
                })}
              </span>
            </>
          )}
          {u.role === "supplier" && u.supplier_id && (
            <span className="px-2 py-0.5 rounded-md bg-stone-100 text-stone-700 border border-stone-300">
              Vinculado a un proveedor
            </span>
          )}
        </div>
      </div>
      <div className="flex flex-col gap-1.5 shrink-0">
        {onProfile && !disabled && (
          <button onClick={onProfile} data-testid={`profile-user-${u.id}`}
            className="sketch-pill bg-[var(--lala-blue)] text-white px-3 py-1 text-xs font-semibold">
            Rol / Alcance
          </button>
        )}
        {onEdit && !disabled && (
          <button onClick={onEdit} data-testid={`edit-user-${u.id}`}
            className="sketch-pill bg-[var(--cream)] text-[var(--lala-blue)] border border-[var(--lala-blue)] px-3 py-1 text-xs font-semibold">
            Editar
          </button>
        )}
        <button onClick={onAudit} data-testid={`audit-user-${u.id}`}
          className="sketch-pill bg-[var(--cream-soft)] text-[var(--ink)] border border-[var(--line)] px-3 py-1 text-xs font-semibold inline-flex items-center gap-1">
          <FileClock size={12} /> Bitácora
        </button>
      </div>
    </div>
  );
}

function permissionDefault(role, key) {
  const defaults = {
    admin:       { can_view: true, can_download_reports: true, can_edit_evaluations: true, can_invite_suppliers: true },
    evaluator:   { can_view: true, can_download_reports: true, can_edit_evaluations: true, can_invite_suppliers: true },
    stakeholder: { can_view: true, can_download_reports: true, can_edit_evaluations: false, can_invite_suppliers: false },
    supplier:    { can_view: true, can_download_reports: true, can_edit_evaluations: false, can_invite_suppliers: false },
  };
  return defaults[role]?.[key] ?? false;
}

function UserEditor({ user, allSuppliers, actorIsAdmin, onCancel, onSaveScope, onSavePerms }) {
  const CATEGORIES = useCategories();
  const [cats, setCats] = useState(user.assigned_categories || []);
  const [pillars, setPillars] = useState(user.assigned_pillars || []);
  const [supIds, setSupIds] = useState(user.assigned_supplier_ids || []);
  const [lead, setLead] = useState(!!user.is_category_lead);
  const [plant, setPlant] = useState(user.plant || "");
  const [plantas, setPlantas] = useState([]);
  useEffect(() => {
    http.get("/plantas").then((r) => setPlantas(r.data.plantas || [])).catch(() => {});
  }, []);
  const [perms, setPerms] = useState(() => {
    const cur = user.permissions || {};
    return Object.fromEntries(
      Object.keys(PERMISSION_META).map((k) =>
        [k, cur[k] !== undefined ? cur[k] : permissionDefault(user.role, k)]
      )
    );
  });
  const [tab, setTab] = useState("scope");
  const [supplierFilter, setSupplierFilter] = useState("");

  const filtered = useMemo(() => {
    return (allSuppliers || []).filter((s) =>
      !supplierFilter || s.name.toLowerCase().includes(supplierFilter.toLowerCase())
    );
  }, [allSuppliers, supplierFilter]);

  const toggleCat = (c) => setCats((cs) => cs.includes(c) ? cs.filter((x) => x !== c) : [...cs, c]);
  const togglePillar = (p) => setPillars((ps) => ps.includes(p) ? ps.filter((x) => x !== p) : [...ps, p]);
  const toggleSup = (id) => setSupIds((s) => s.includes(id) ? s.filter((x) => x !== id) : [...s, id]);

  const submitScope = async () => {
    if (actorIsAdmin && (plant || "") !== (user.plant || "")) {
      await http.put(`/admin/users/${user.id}/plant`, { plant: plant || null });
    }
    onSaveScope({
      assigned_categories: cats,
      assigned_supplier_ids: supIds,
      ...(plant ? {} : { assigned_pillars: pillars }),
      ...(actorIsAdmin ? { is_category_lead: lead } : {}),
    });
  };
  const submitPerms = () => onSavePerms(perms);

  return (
    <Modal testId="user-editor-modal" backdrop="bg-black/50">
      <div className="bg-[var(--cream)] sketch-border w-full max-w-3xl">
        <div className="flex items-center justify-between p-5 border-b border-[var(--line)]">
          <div>
            <div className="font-display text-2xl">{user.name}</div>
            <div className="text-xs text-[var(--ink-soft)]">{user.email} · {ROLE_META[user.role]?.label}</div>
          </div>
          <button onClick={onCancel} data-testid="editor-close" className="hover:bg-[var(--cream-soft)] p-1 rounded">
            <X size={20} />
          </button>
        </div>

        <div className="flex gap-1 px-5 pt-4">
          <button onClick={() => setTab("scope")} data-testid="tab-scope"
            className={`sketch-pill px-4 py-1.5 text-xs font-semibold ${tab === "scope" ? "bg-[var(--lala-blue)] text-[var(--cream)]" : "bg-[var(--cream-soft)] text-[var(--lala-blue)]"}`}>
            Scope (qué ve)
          </button>
          <button onClick={() => setTab("perms")} data-testid="tab-perms"
            className={`sketch-pill px-4 py-1.5 text-xs font-semibold ${tab === "perms" ? "bg-[var(--lala-blue)] text-[var(--cream)]" : "bg-[var(--cream-soft)] text-[var(--lala-blue)]"}`}>
            Permisos (qué puede hacer)
          </button>
        </div>

        <div className="p-5">
          {tab === "scope" && (
            <div className="space-y-5">
              <div>
                <label className="text-xs uppercase tracking-wider text-[var(--ink-soft)] font-semibold">Categorías asignadas</label>
                <div className="flex flex-wrap gap-2 mt-1.5" data-testid="cat-selector">
                  {CATEGORIES.map((c) => {
                    const sel = cats.includes(c);
                    return (
                      <button key={c} type="button" onClick={() => toggleCat(c)}
                        data-testid={`cat-${c.replace(/\s+/g, "-")}`}
                        className={`sketch-pill px-3 py-1 text-xs font-semibold ${sel ? "bg-[var(--lala-blue)] text-[var(--cream)]" : "bg-[var(--cream-soft)] text-[var(--lala-blue)] border border-[var(--lala-blue)]/40"}`}>
                        {c}
                      </button>
                    );
                  })}
                </div>
                <div className="text-xs text-[var(--ink-soft)] mt-2 italic">
                  Si no marcas ninguna y no agregas proveedores específicos, el usuario verá todos los proveedores.
                </div>
              </div>

              <div>
                <label className="text-xs uppercase tracking-wider text-[var(--ink-soft)] font-semibold">Pilares asignados (qué alimenta en Captura mensual)</label>
                <div className="flex flex-wrap gap-2 mt-1.5" data-testid="pillar-selector">
                  {PILLARS.map((p) => {
                    const sel = pillars.includes(p.key);
                    return (
                      <button key={p.key} type="button" onClick={() => togglePillar(p.key)}
                        data-testid={`pillar-${p.key}`}
                        className={`sketch-pill px-3 py-1 text-xs font-semibold ${sel ? "bg-[var(--lala-blue)] text-[var(--cream)]" : "bg-[var(--cream-soft)] text-[var(--lala-blue)] border border-[var(--lala-blue)]/40"}`}>
                        ◆ {p.label}
                      </button>
                    );
                  })}
                </div>
                <div className="text-xs text-[var(--ink-soft)] mt-2 italic">
                  Un evaluador con pilares asignados solo verá y capturará indicadores de esos pilares.
                  Si no marcas ninguno, podrá alimentar todos los pilares.
                </div>
              </div>

              {user.role === "evaluator" && (
                <div>
                  <label className="text-xs uppercase tracking-wider text-[var(--ink-soft)] font-semibold">Planta (evaluador de Nivel de Servicio)</label>
                  <select value={plant} onChange={(e) => setPlant(e.target.value)}
                    className="input-field w-full mt-1.5 max-w-sm" data-testid="plant-selector">
                    <option value="">Sin planta (evaluador normal)</option>
                    {plantas.map((p) => <option key={p} value={p}>{p}</option>)}
                  </select>
                  <div className="text-xs text-[var(--ink-soft)] mt-2 italic">
                    Al asignar una planta, este evaluador queda limitado ÚNICAMENTE al pilar Nivel de Servicio y sus
                    calificaciones alimentan el promedio ponderado entre plantas.
                  </div>
                </div>
              )}

              <div>
                <label className="text-xs uppercase tracking-wider text-[var(--ink-soft)] font-semibold">Proveedores específicos (overrides)</label>
                <input type="text" value={supplierFilter}
                  onChange={(e) => setSupplierFilter(e.target.value)}
                  placeholder="Buscar proveedor…"
                  className="input-field w-full mt-1.5 mb-2" data-testid="supplier-search" />
                <div className="max-h-48 overflow-y-auto sketch-border-soft bg-[var(--cream-soft)] p-2">
                  {filtered.map((s) => (
                    <label key={s.id} className="flex items-center gap-2 text-sm p-1 hover:bg-[var(--cream)] rounded cursor-pointer">
                      <input type="checkbox" checked={supIds.includes(s.id)}
                        onChange={() => toggleSup(s.id)}
                        data-testid={`sup-${s.id}`}
                        className="accent-[var(--lala-blue)]" />
                      <span className="flex-1">{s.name}</span>
                      <span className="text-xs text-[var(--ink-soft)]">{s.category}</span>
                    </label>
                  ))}
                  {filtered.length === 0 && (
                    <div className="text-xs text-[var(--ink-soft)] italic p-2">Sin resultados.</div>
                  )}
                </div>
                <div className="text-xs text-[var(--ink-soft)] mt-1">{supIds.length} proveedor{supIds.length === 1 ? "" : "es"} seleccionado{supIds.length === 1 ? "" : "s"}.</div>
              </div>

              {actorIsAdmin && user.role !== "supplier" && (
                <div className="sketch-border-soft bg-amber-50 p-3 flex items-start gap-2">
                  <Star size={16} className="text-amber-700 mt-0.5 shrink-0" />
                  <div className="flex-1">
                    <label className="inline-flex items-center gap-2 text-sm cursor-pointer" data-testid="lead-toggle">
                      <input type="checkbox" checked={lead} onChange={(e) => setLead(e.target.checked)}
                        className="accent-amber-600" />
                      <span className="font-semibold">Líder de categoría</span>
                    </label>
                    <div className="text-xs text-amber-900 mt-0.5">
                      Le permite administrar otros usuarios cuyas categorías sean subconjunto de las suyas.
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}

          {tab === "perms" && (
            <div className="space-y-3">
              {Object.entries(PERMISSION_META).map(([k, m]) => {
                const Icon = m.icon;
                return (
                  <label key={k}
                    className="flex items-center gap-3 p-3 sketch-border-soft bg-[var(--cream-soft)] cursor-pointer"
                    data-testid={`perm-${k}`}>
                    <input type="checkbox" checked={!!perms[k]}
                      onChange={(e) => setPerms({ ...perms, [k]: e.target.checked })}
                      className="accent-[var(--lala-blue)] w-4 h-4" />
                    <Icon size={18} className="text-[var(--lala-blue)]" />
                    <div className="flex-1">
                      <div className="font-semibold text-sm">{m.label}</div>
                      <div className="text-xs text-[var(--ink-soft)]">
                        {permissionDescription(k)}
                      </div>
                    </div>
                  </label>
                );
              })}
              <div className="text-xs text-[var(--ink-soft)] italic mt-2 inline-flex items-center gap-1">
                <AlertCircle size={12} /> Estos permisos sobre-escriben los defaults del rol.
              </div>
            </div>
          )}
        </div>
        <div className="flex items-center justify-end gap-2 p-5 border-t border-[var(--line)] bg-[var(--cream-soft)]">
          <button onClick={onCancel} data-testid="editor-cancel"
            className="sketch-pill bg-[var(--cream)] text-[var(--lala-blue)] border border-[var(--lala-blue)] px-4 py-1.5 text-sm font-semibold">
            Cancelar
          </button>
          <button onClick={tab === "scope" ? submitScope : submitPerms}
            data-testid="editor-save"
            className="btn-primary inline-flex items-center gap-2">
            <Save size={14} /> Guardar {tab === "scope" ? "scope" : "permisos"}
          </button>
        </div>
      </div>
    </Modal>
  );
}

function permissionDescription(key) {
  switch (key) {
    case "can_view": return "Acceso de lectura al dashboard y proveedores asignados.";
    case "can_download_reports": return "Descargar reportes ejecutivos PDF de los proveedores asignados.";
    case "can_edit_evaluations": return "Crear, editar y publicar scorecards mensuales.";
    case "can_invite_suppliers": return "Generar links de invitación y onboarding para nuevos proveedores.";
    default: return "";
  }
}

function AuditDrawer({ user, rows, onClose }) {
  return (
    <Modal testId="audit-drawer" align="end" backdrop="bg-black/50">
      <div className="bg-[var(--cream)] sketch-border w-full max-w-xl h-full overflow-y-auto">
        <div className="flex items-center justify-between p-5 border-b border-[var(--line)] sticky top-0 bg-[var(--cream)]">
          <div>
            <div className="font-display text-2xl">Bitácora · {user.name}</div>
            <div className="text-xs text-[var(--ink-soft)]">{rows.length} eventos recientes</div>
          </div>
          <button onClick={onClose} data-testid="audit-close" className="hover:bg-[var(--cream-soft)] p-1 rounded">
            <X size={20} />
          </button>
        </div>
        <div className="p-5 space-y-2">
          {rows.length === 0 && <div className="text-[var(--ink-soft)] italic">Sin eventos registrados.</div>}
          {rows.map((r) => (
            <div key={r.id} className="sketch-border-soft bg-[var(--cream-soft)] p-3 text-sm" data-testid={`audit-row-${r.id}`}>
              <div className="flex items-center gap-2">
                <span className="font-semibold">{r.action}</span>
                <span className="text-xs text-[var(--ink-soft)]">·</span>
                <span className="text-xs text-[var(--ink-soft)]">{r.resource_type}{r.resource_id ? ` ${String(r.resource_id).slice(0, 8)}` : ""}</span>
              </div>
              <div className="text-xs text-[var(--ink-soft)] mt-0.5">{new Date(r.at).toLocaleString()}</div>
              {r.meta && Object.keys(r.meta).length > 0 && (
                <pre className="text-[10px] text-stone-600 mt-1 whitespace-pre-wrap break-all bg-white/50 p-1.5 rounded">
                  {JSON.stringify(r.meta, null, 0)}
                </pre>
              )}
            </div>
          ))}
        </div>
      </div>
    </Modal>
  );
}
